import { useState } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import {
  BUSINESS_ENTITY_TYPES,
  CHARITABLE_ENTITY_TYPES,
  GRANTOR_ENTITY_TYPES,
} from '@/modules/entities/entities.constants';
import { CreateEntityShortFormModal } from '@/modules/entities/EntityShortFormModal/CreateEntityShortFormModal';
import { TestamentaryEntityModal } from '@/modules/entities/testamentaryEntities/TestamentaryEntityModal';
import { AddHoldingsFormModal } from '@/modules/estate/components/AddHoldingsFormModal/AddHoldingsFormModal';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

interface EstateCreateButtonProps {
  householdId: string;
  /** Hide the "Create sub-trust or inherited account" menu item. Useful when
   *  creating a sub-trust alone would not satisfy the parent context (e.g. the
   *  Ownership diagram requires a top-level entity). */
  hideSubTrustOption?: boolean;
}

/**
 * Shared "Create" popover button with menu items for adding holdings, trusts,
 * sub-trusts, and businesses. Used in the Estate page header and in empty
 * states that need the full create menu (Ownership, All entities).
 */
export function EstateCreateButton({
  householdId,
  hideSubTrustOption,
}: EstateCreateButtonProps) {
  const { isTwoClientHousehold, primaryClients } = useHouseholdDetailsContext();
  const [showAddHoldingModal, setShowAddHoldingModal] = useState(false);
  const [showCreateTrustModal, setShowCreateTrustModal] = useState(false);
  const [showCreateSubTrustModal, setShowCreateSubTrustModal] = useState(false);
  const [showCreateBusinessModal, setShowCreateBusinessModal] = useState(false);

  return (
    <>
      <ButtonWithPopover
        data-testid="estate-page-global-create-button"
        label="Create"
        size="sm"
        variant="primary"
        startIcon={PlusIcon}
        popperVariant="menuBelow"
      >
        <MenuItem
          dataTestId="estate-page-global-add-holdings-button"
          label="Add holdings"
          onClick={() => setShowAddHoldingModal(true)}
        />
        <MenuItem
          label="Create trust"
          onClick={() => setShowCreateTrustModal(true)}
        />
        {!hideSubTrustOption && (
          <MenuItem
            label="Create sub-trust or inherited account"
            onClick={() => setShowCreateSubTrustModal(true)}
          />
        )}
        <MenuItem
          label="Create business"
          onClick={() => setShowCreateBusinessModal(true)}
        />
      </ButtonWithPopover>

      {showAddHoldingModal && (
        <AddHoldingsFormModal
          open={showAddHoldingModal}
          onClose={() => setShowAddHoldingModal(false)}
          holdingsGroup={undefined}
        />
      )}

      {showCreateTrustModal && (
        <CreateEntityShortFormModal
          isOpen={showCreateTrustModal}
          onClose={() => setShowCreateTrustModal(false)}
          householdId={householdId}
          limitEntityTypes={[
            ...GRANTOR_ENTITY_TYPES,
            ...CHARITABLE_ENTITY_TYPES,
          ]}
          heading="Create trust"
          entityTypeLabel="Trust type"
          buttonText="Create trust"
        />
      )}

      {showCreateSubTrustModal && (
        <TestamentaryEntityModal
          isOpen={showCreateSubTrustModal}
          onClose={() => setShowCreateSubTrustModal(false)}
          dyingPrimaryClientId={primaryClients?.[0]?.id || null}
          deadClientIds={[]}
          isTwoClientHousehold={isTwoClientHousehold}
        />
      )}

      {showCreateBusinessModal && (
        <CreateEntityShortFormModal
          isOpen={showCreateBusinessModal}
          onClose={() => setShowCreateBusinessModal(false)}
          householdId={householdId}
          limitEntityTypes={[...BUSINESS_ENTITY_TYPES]}
          heading="Create business"
          entityTypeLabel="Business type"
          buttonText="Create business"
        />
      )}
    </>
  );
}
